import React, { Profiler, useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Typography,
  Chip,
  useTheme,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import CustomDateSelector from "components/CustomDateSelector";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import { useWebSocket } from "contexts/WebSocketContext";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const TIMESHEET_DESKTOP_COLUMNS =
  "minmax(115px, 1fr) minmax(115px, 1fr) minmax(95px, 0.8fr) minmax(110px, 0.9fr) minmax(260px, 1.8fr)";
const TIMESHEET_DAY_COLUMN_WIDTH = "260px";
const TIMESHEETS_PERF_QUERY_PARAM = "timesheetsPerf";
const TIMESHEETS_PERF_STORAGE_KEY = "ce_portal.timesheetsPerf";
const TIMESHEETS_DETAILS_PANEL_STATE_STORAGE_KEY = "ce_portal.timesheets.detailsPanelExpanded";
const TIMESHEETS_PERF_LOG_THRESHOLD_MS = 8;
const TIMESHEETS_EVENT_LOOP_LAG_THRESHOLD_MS = 200;

const formatDateOnly = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatTimeOnly = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleTimeString();
};

const getDurationLabel = (clockInAt, clockOutAt) => {
  if (!clockInAt || !clockOutAt) return "In progress";

  const start = new Date(clockInAt);
  const end = new Date(clockOutAt);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "-";
  }

  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return "0m";

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
};

const getLiveDurationLabel = (clockInAt, nowValue) => {
  if (!clockInAt || !nowValue) return "In progress";

  const start = new Date(clockInAt);
  const end = new Date(nowValue);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "In progress";
  }

  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return "0m";

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
};

const getSafeLiveEndTime = (clockInAt, nowValue) => {
  const start = clockInAt ? new Date(clockInAt) : null;
  const nowDate = nowValue ? new Date(nowValue) : null;

  if (!start || Number.isNaN(start.getTime())) {
    return nowDate && !Number.isNaN(nowDate.getTime()) ? nowDate : null;
  }

  if (!nowDate || Number.isNaN(nowDate.getTime())) {
    return start;
  }

  return nowDate.getTime() < start.getTime() ? start : nowDate;
};

const formatMinutesAsHHmm = (minutesValue) => {
  const safeMinutes = Number.isFinite(minutesValue) ? Math.max(0, Math.round(minutesValue)) : 0;
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const getWorkdaysInMonth = (year, monthIndexZeroBased) => {
  if (!Number.isInteger(year) || !Number.isInteger(monthIndexZeroBased) || monthIndexZeroBased < 0 || monthIndexZeroBased > 11) {
    return null;
  }

  const daysInMonth = new Date(year, monthIndexZeroBased + 1, 0).getDate();
  let workdays = 0;

  for (let day = 1; day <= daysInMonth; day += 1) {
    const weekday = new Date(year, monthIndexZeroBased, day).getDay();
    if (weekday !== 0 && weekday !== 6) {
      workdays += 1;
    }
  }

  return workdays;
};

const getTotalDurationLabel = (entries) => {
  const totalMs = entries.reduce((sum, entry) => {
    if (!entry.clockInAt || !entry.clockOutAt) {
      return sum;
    }

    const start = new Date(entry.clockInAt);
    const end = new Date(entry.clockOutAt);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return sum;
    }

    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) {
      return sum;
    }

    return sum + diffMs;
  }, 0);

  const totalMinutes = Math.floor(totalMs / 60000);

  const hasInProgress = entries.some((entry) => !entry.clockOutAt);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let label = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  if (totalMinutes === 0 && !hasInProgress) {
    label = "0m";
  }

  return hasInProgress ? `${label} + in progress` : label;
};

const TimesheetsTab = forwardRef(({ isMobile, hideHeader = false, useOuterGridCard = false, hourlyRate = 0, onDateRangePropsChange = null, memberId = null, memberName = "", allowEdit = true, inlineStatsAndFilter = false }, ref) => {
  const theme = useTheme();
  const { socket } = useWebSocket();
  const { setOverlayData, setOverlayName } = useStateContext();

  const [timesheets, setTimesheets] = useState([]);
  const [loadingTimesheets, setLoadingTimesheets] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [monthsByYear, setMonthsByYear] = useState({});
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [now, setNow] = useState(() => new Date());
  const [detailsPanelExpanded, setDetailsPanelExpanded] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }

    const rawValue = window.localStorage.getItem(TIMESHEETS_DETAILS_PANEL_STATE_STORAGE_KEY);
    if (rawValue === "false") {
      return false;
    }

    return true;
  });
  const lastDateRangeSignatureRef = useRef("");

  const normalizedMemberId = useMemo(() => {
    const parsed = Number.parseInt(String(memberId || ""), 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [memberId]);

  const timesheetsBasePath = useMemo(() => {
    return normalizedMemberId
      ? `/timesheets/member/${normalizedMemberId}`
      : "/timesheets/me";
  }, [normalizedMemberId]);

  const perfEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;

    const params = new URLSearchParams(window.location.search);
    if (params.get(TIMESHEETS_PERF_QUERY_PARAM) === "1") {
      return true;
    }

    return window.localStorage.getItem(TIMESHEETS_PERF_STORAGE_KEY) === "1";
  }, []);

  const measurePerf = useCallback((label, compute) => {
    if (!perfEnabled || typeof performance === "undefined") {
      return compute();
    }

    const startedAt = performance.now();
    const result = compute();
    const elapsedMs = performance.now() - startedAt;

    if (elapsedMs >= TIMESHEETS_PERF_LOG_THRESHOLD_MS) {
      console.info(`[TimesheetsPerf] ${label}: ${elapsedMs.toFixed(2)}ms`);
    }

    return result;
  }, [perfEnabled]);

  const onTimesheetsProfilerRender = useCallback((id, phase, actualDuration, baseDuration, startTime, commitTime) => {
    if (!perfEnabled) return;

    if (actualDuration >= TIMESHEETS_PERF_LOG_THRESHOLD_MS) {
      console.info(
        `[TimesheetsPerf][Profiler] ${id} ${phase} actual=${actualDuration.toFixed(2)}ms base=${baseDuration.toFixed(2)}ms start=${startTime.toFixed(2)} commit=${commitTime.toFixed(2)}`
      );
    }
  }, [perfEnabled]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(new Date());
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.localStorage.setItem(
      TIMESHEETS_DETAILS_PANEL_STATE_STORAGE_KEY,
      String(detailsPanelExpanded),
    );
  }, [detailsPanelExpanded]);

  useEffect(() => {
    if (!perfEnabled || typeof window === "undefined") return undefined;

    console.info(
      `[TimesheetsPerf] enabled (${TIMESHEETS_PERF_QUERY_PARAM}=1 or localStorage ${TIMESHEETS_PERF_STORAGE_KEY}=1)`
    );

    let longTaskObserver = null;
    if (typeof PerformanceObserver !== "undefined" && PerformanceObserver.supportedEntryTypes?.includes("longtask")) {
      longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          console.warn(
            `[TimesheetsPerf][LongTask] ${entry.duration.toFixed(2)}ms at ${entry.startTime.toFixed(2)}ms`
          );
        });
      });
      longTaskObserver.observe({ entryTypes: ["longtask"] });
    }

    let expectedAt = Date.now() + 1000;
    const lagInterval = window.setInterval(() => {
      const nowTs = Date.now();
      const lagMs = nowTs - expectedAt;
      if (lagMs >= TIMESHEETS_EVENT_LOOP_LAG_THRESHOLD_MS) {
        console.warn(`[TimesheetsPerf][EventLoopLag] ${lagMs}ms`);
      }
      expectedAt = nowTs + 1000;
    }, 1000);

    return () => {
      window.clearInterval(lagInterval);
      if (longTaskObserver) {
        longTaskObserver.disconnect();
      }
    };
  }, [perfEnabled]);

  const fetchDateOptions = useCallback(async () => {
    try {
      const response = await api.get(`${timesheetsBasePath}/date-options`);
      const payload = response?.data || {};

      const yearsWithData = Array.isArray(payload.availableYears)
        ? payload.availableYears.map((year) => String(year))
        : [];

      const monthMap = Object.entries(payload.monthsByYear || {}).reduce(
        (acc, [year, months]) => {
          acc[String(year)] = Array.isArray(months)
            ? months
                .filter((month) => MONTH_NAMES.includes(month))
                .sort(
                  (left, right) =>
                    MONTH_NAMES.indexOf(left) - MONTH_NAMES.indexOf(right),
                )
            : [];
          return acc;
        },
        {},
      );

      setAvailableYears(yearsWithData);
      setMonthsByYear(monthMap);

      if (yearsWithData.length > 0) {
        const latestYear = yearsWithData[yearsWithData.length - 1];
        const latestYearMonths = monthMap[latestYear] || [];
        const latestMonth = latestYearMonths[latestYearMonths.length - 1];

        setSelectedYears([latestYear]);
        setSelectedMonths(latestMonth ? [latestMonth] : []);
        return;
      }

      setSelectedYears([]);
      setSelectedMonths([]);
    } catch (error) {
      setAvailableYears([]);
      setMonthsByYear({});
      setSelectedYears([]);
      setSelectedMonths([]);
    }
  }, [timesheetsBasePath]);

  const fetchTimesheets = useCallback(async () => {
    const selectedYear = selectedYears[0];
    if (!selectedYear) {
      setTimesheets([]);
      return;
    }

    setLoadingTimesheets(true);
    try {
      const params = { year: selectedYear };
      if (selectedMonths[0]) {
        params.months = selectedMonths[0];
      }

      const response = await api.get(timesheetsBasePath, { params });
      setTimesheets(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setTimesheets([]);
    } finally {
      setLoadingTimesheets(false);
    }
  }, [selectedMonths, selectedYears, timesheetsBasePath]);

  useEffect(() => {
    fetchDateOptions();
  }, [fetchDateOptions]);

  useEffect(() => {
    const selectedYear = selectedYears[0];
    if (!selectedYear) return;

    const yearMonths = Array.isArray(monthsByYear[selectedYear])
      ? monthsByYear[selectedYear]
      : [];

    const selectedMonth = selectedMonths[0];
    if (selectedMonth && !yearMonths.includes(selectedMonth)) {
      setSelectedMonths(yearMonths.length > 0 ? [yearMonths[0]] : []);
    }
  }, [monthsByYear, selectedMonths, selectedYears]);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  // Listen for timesheet updates via WebSocket
  useEffect(() => {
    if (!socket) return;

    const handleTimesheetUpdate = async () => {
      // Refresh both date options and timesheets when an update occurs
      await fetchDateOptions();
      await fetchTimesheets();
    };

    socket.on("timesheet:updated", handleTimesheetUpdate);
    return () => socket.off("timesheet:updated", handleTimesheetUpdate);
  }, [socket, fetchDateOptions, fetchTimesheets]);

  const selectorMonths = useMemo(() => {
    const monthSet = new Set();

    Object.values(monthsByYear).forEach((months) => {
      (Array.isArray(months) ? months : []).forEach((month) => {
        if (MONTH_NAMES.includes(month)) {
          monthSet.add(month);
        }
      });
    });

    return MONTH_NAMES.filter((month) => monthSet.has(month));
  }, [monthsByYear]);

  const handleDateSelectionSave = useCallback((selection) => {
    const nextYears = Array.isArray(selection?.years)
      ? selection.years.map((year) => String(year)).filter(Boolean)
      : [];

    const yearValue = nextYears[0] || "";
    const validMonthsForYear = yearValue
      ? Array.isArray(monthsByYear[yearValue])
        ? monthsByYear[yearValue]
        : []
      : [];

    const requestedMonth = Array.isArray(selection?.months)
      ? selection.months[0]
      : null;

    const nextMonth = requestedMonth && validMonthsForYear.includes(requestedMonth)
      ? requestedMonth
      : validMonthsForYear[0] || null;

    setSelectedYears(yearValue ? [yearValue] : []);
    setSelectedMonths(nextMonth ? [nextMonth] : []);
  }, [monthsByYear]);

  const handleEditTimeslot = useCallback((entry) => {
    setOverlayData({ timesheet: entry });
    setOverlayName(OVERLAY_NAMES.SETTINGS_TIMESHEETS_MANUAL_NEW);
  }, [setOverlayData, setOverlayName]);

  const rows = useMemo(
    () =>
      measurePerf("rows", () =>
        timesheets.map((entry, index) => {
          const liveEndTime = getSafeLiveEndTime(entry.ClockInAt, now);

          return {
            id: entry.id ?? `${entry.ClockInAt || "row"}-${index}`,
            rawEntry: entry,
            date: entry.ClockInAt || entry.ClockOutAt || null,
            clockInAt: entry.ClockInAt,
            clockOutAt: entry.ClockOutAt,
            liveClockOutAt: liveEndTime,
            duration: entry.ClockOutAt
              ? getDurationLabel(entry.ClockInAt, entry.ClockOutAt)
              : getLiveDurationLabel(entry.ClockInAt, liveEndTime),
            status: entry.ClockOutAt ? "Completed" : "In progress",
            notes: entry.Notes || "-",
          };
        }),
      ),
    [measurePerf, now, timesheets],
  );

  const numericHourlyRate = useMemo(() => {
    const parsed = Number(hourlyRate);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [hourlyRate]);

  const totalMinutesForSelectedPeriod = useMemo(() => {
    return measurePerf("totalMinutesForSelectedPeriod", () =>
      timesheets.reduce((sum, entry) => {
        const start = entry?.ClockInAt ? new Date(entry.ClockInAt) : null;
        const end = entry?.ClockOutAt ? new Date(entry.ClockOutAt) : now;

        if (!start || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          return sum;
        }

        const diffMs = end.getTime() - start.getTime();
        if (diffMs <= 0) {
          return sum;
        }

        return sum + (diffMs / 60000);
      }, 0),
    );
  }, [measurePerf, now, timesheets]);

  const monthlyTotalAmount = useMemo(() => {
    return measurePerf("monthlyTotalAmount", () =>
      timesheets.reduce((sum, entry) => {
        const start = entry?.ClockInAt ? new Date(entry.ClockInAt) : null;
        const end = entry?.ClockOutAt ? new Date(entry.ClockOutAt) : now;

        if (!start || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          return sum;
        }

        const diffMs = end.getTime() - start.getTime();
        if (diffMs <= 0) {
          return sum;
        }

        const minutes = diffMs / 60000;
        const rowRateParsed = Number(entry?.HourlyRateSnapshot);
        const effectiveRate = Number.isFinite(rowRateParsed) && rowRateParsed > 0
          ? rowRateParsed
          : numericHourlyRate;

        return sum + ((minutes / 60) * effectiveRate);
      }, 0),
    );
  }, [measurePerf, now, numericHourlyRate, timesheets]);

  const monthlyTotalLabel = useMemo(() => {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(monthlyTotalAmount);
  }, [monthlyTotalAmount]);

  const monthlyStats = useMemo(() => {
    return measurePerf("monthlyStats", () => {
      const rowDurationsInMinutes = rows
        .map((row) => {
          const start = row.clockInAt ? new Date(row.clockInAt) : null;
          const end = row.clockOutAt ? new Date(row.clockOutAt) : now;

          if (!start || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            return null;
          }

          const diffMs = end.getTime() - start.getTime();
          if (diffMs <= 0) {
            return 0;
          }

          return diffMs / 60000;
        })
        .filter((minutes) => Number.isFinite(minutes));

      const sortedDurations = [...rowDurationsInMinutes].sort((left, right) => left - right);
      const middleIndex = Math.floor(sortedDurations.length / 2);

      const medianMinutes = sortedDurations.length === 0
        ? 0
        : sortedDurations.length % 2 === 0
          ? Math.round((sortedDurations[middleIndex - 1] + sortedDurations[middleIndex]) / 2)
          : sortedDurations[middleIndex];

      const dayTotals = rows.reduce((acc, row) => {
        const dateValue = row.date ? new Date(row.date) : null;
        if (!dateValue || Number.isNaN(dateValue.getTime())) {
          return acc;
        }

        const dayKey = dateValue.toISOString().slice(0, 10);
        const start = row.clockInAt ? new Date(row.clockInAt) : null;
        const end = row.clockOutAt ? new Date(row.clockOutAt) : now;
        if (!start || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          return acc;
        }

        const diffMs = end.getTime() - start.getTime();
        const minutes = diffMs <= 0 ? 0 : diffMs / 60000;
        acc[dayKey] = (acc[dayKey] || 0) + minutes;
        return acc;
      }, {});

      const workedDays = Object.keys(dayTotals).length;
      const avgPerWorkedDayMinutes = workedDays > 0
        ? Math.round(totalMinutesForSelectedPeriod / workedDays)
        : 0;

      return {
        totalMinutes: totalMinutesForSelectedPeriod,
        medianMinutesPerEntry: medianMinutes,
        avgMinutesPerWorkedDay: avgPerWorkedDayMinutes,
        workedDays,
        entryCount: rows.length,
      };
    });
  }, [measurePerf, now, rows, totalMinutesForSelectedPeriod]);

  const monthlyStatsLabels = useMemo(() => {
    const selectedYear = Number.parseInt(selectedYears?.[0] || "", 10);
    const selectedMonthIndex = MONTH_NAMES.indexOf(selectedMonths?.[0] || "");
    const monthWorkdays = getWorkdaysInMonth(selectedYear, selectedMonthIndex);
    const workedDaysLabel = Number.isInteger(monthWorkdays)
      ? `${monthlyStats.workedDays}/${monthWorkdays}`
      : String(monthlyStats.workedDays);

    return {
      totalHours: formatMinutesAsHHmm(monthlyStats.totalMinutes),
      medianPerEntry: formatMinutesAsHHmm(monthlyStats.medianMinutesPerEntry),
      averagePerWorkedDay: formatMinutesAsHHmm(monthlyStats.avgMinutesPerWorkedDay),
      workedDays: workedDaysLabel,
      entryCount: String(monthlyStats.entryCount),
    };
  }, [monthlyStats, selectedMonths, selectedYears]);

  const timesheetStatsCards = useMemo(() => {
    return [
      {
        key: "month-total",
        label: "Month Total",
        value: monthlyTotalLabel,
        icon: PaidRoundedIcon,
        color: theme.palette.primary.main,
        valueVariant: "h3",
      },
      {
        key: "total-hours",
        label: "Total Hours",
        value: monthlyStatsLabels.totalHours,
        icon: AccessTimeRoundedIcon,
        color: theme.palette.secondary.main,
        valueVariant: "h4",
      },
      {
        key: "median",
        label: "Median So Far",
        value: monthlyStatsLabels.medianPerEntry,
        icon: BarChartRoundedIcon,
        color: "#ff9800",
        valueVariant: "h4",
      },
      {
        key: "average",
        label: "Avg/Worked Day",
        value: monthlyStatsLabels.averagePerWorkedDay,
        icon: CalendarTodayRoundedIcon,
        color: "#f44336",
        valueVariant: "h4",
      },
      {
        key: "worked-days",
        label: "Worked Days",
        value: monthlyStatsLabels.workedDays,
        icon: LocalFireDepartmentRoundedIcon,
        color: theme.palette.primary.main,
        valueVariant: "h4",
      },
      {
        key: "entries",
        label: "Entries",
        value: monthlyStatsLabels.entryCount,
        icon: FormatListNumberedRoundedIcon,
        color: theme.palette.secondary.main,
        valueVariant: "h4",
      },
    ];
  }, [
    monthlyTotalLabel,
    monthlyStatsLabels.averagePerWorkedDay,
    monthlyStatsLabels.entryCount,
    monthlyStatsLabels.medianPerEntry,
    monthlyStatsLabels.totalHours,
    monthlyStatsLabels.workedDays,
    theme.palette.primary.main,
    theme.palette.secondary.main,
  ]);

  const groupedRows = useMemo(() => {
    return measurePerf("groupedRows", () => {
      const groups = rows.reduce((acc, row) => {
        const groupKey = row.date ? formatDateOnly(row.date) : "Undated";
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(row);
        return acc;
      }, {});

      return Object.entries(groups).map(([dateLabel, entries]) => ({
        dateLabel,
        entries: [...entries].sort((left, right) => {
          const leftTime = left.clockInAt ? new Date(left.clockInAt).getTime() : 0;
          const rightTime = right.clockInAt ? new Date(right.clockInAt).getTime() : 0;
          return rightTime - leftTime;
        }),
        totalDurationLabel: getTotalDurationLabel(entries),
      }));
    });
  }, [measurePerf, rows]);

  const dateRangeSelector = useMemo(() => ({
    label: "Date Range",
    months: selectorMonths,
    years: availableYears,
    selectedMonths,
    selectedYears,
    enabledYears: availableYears,
    enabledMonthsByYear: monthsByYear,
    onSave: handleDateSelectionSave,
    isMultiMonth: false,
    isMultiYear: false,
    monthGridItemSize: 3,
    yearGridItemSize: 4,
    showClearFilter: false,
    useInnerShadow: false,
  }), [
    selectorMonths,
    availableYears,
    selectedMonths,
    selectedYears,
    monthsByYear,
    handleDateSelectionSave,
  ]);

  const shouldRenderTopStatsAndFilter = inlineStatsAndFilter === true;
  const useActivityMatchedStyling = shouldRenderTopStatsAndFilter;
  const hourlyRateSubject = useMemo(() => {
    const trimmedName = String(memberName || "").trim();
    if (!trimmedName) {
      return "Your";
    }

    const endsWithS = /s$/i.test(trimmedName);
    return endsWithS ? `${trimmedName}'` : `${trimmedName}'s`;
  }, [memberName]);

  useEffect(() => {
    if (typeof onDateRangePropsChange === "function") {
      const signature = JSON.stringify({
        months: selectorMonths,
        years: availableYears,
        selectedMonths,
        selectedYears,
        enabledMonthsByYear: monthsByYear,
      });

      if (signature !== lastDateRangeSignatureRef.current) {
        lastDateRangeSignatureRef.current = signature;
        onDateRangePropsChange(dateRangeSelector);
      }
    }
  }, [
    onDateRangePropsChange,
    dateRangeSelector,
    selectorMonths,
    availableYears,
    selectedMonths,
    selectedYears,
    monthsByYear,
  ]);

  useImperativeHandle(ref, () => ({
    getDateRangeSelector: () => dateRangeSelector,
    refreshTimesheets: async () => {
      await fetchDateOptions();
      await fetchTimesheets();
    },
    getMonthlyTotal: () => ({
      amount: monthlyTotalAmount,
      label: monthlyTotalLabel,
      minutes: totalMinutesForSelectedPeriod,
    }),
  }), [
    dateRangeSelector,
    fetchDateOptions,
    fetchTimesheets,
    monthlyTotalAmount,
    monthlyTotalLabel,
    totalMinutesForSelectedPeriod,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box
        borderRadius={useOuterGridCard ? undefined : "12px"}
        bgcolor={useOuterGridCard ? undefined : theme.palette.background.default}
        sx={{
          boxShadow: useOuterGridCard ? "none" : theme.shadows[3],
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {shouldRenderTopStatsAndFilter && (
          <Box
            sx={{
              flexShrink: 0,
              borderBottom: `1px solid ${theme.palette.divider}`,
              //backgroundColor: theme.palette.background.alt,
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 1.5, sm: 2 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            {numericHourlyRate <= 0 && (
              <Typography
                variant="body2"
                fontWeight={700}
                color={theme.palette.warning.main}
                sx={{ textAlign: "center" }}
              >
                {hourlyRateSubject} hourly rate is currently $0.00. Please update the rate in Settings to ensure totals are calculated correctly.
              </Typography>
            )}
            <Grid container spacing={2} sx={{ width: "100%" }}>
              {timesheetStatsCards.map((statCard) => {
                const StatIcon = statCard.icon;
                return (
                  <Grid key={statCard.key} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card
                      elevation={2}
                      sx={{
                        borderRadius: "12px",
                        height: "100%",
                      }}
                    >
                      <CardContent
                        sx={{
                          textAlign: "center",
                          minHeight: 156,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <StatIcon
                          sx={{
                            fontSize: 40,
                            color: statCard.color,
                            mb: 1,
                          }}
                        />
                        <Typography variant={statCard.valueVariant} fontWeight="bold">
                          {statCard.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {statCard.label}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {shouldRenderTopStatsAndFilter && (
          <Box
            sx={{
              flexShrink: 0,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1, sm: 1.25 },
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <CustomDateSelector {...dateRangeSelector} useSimpleStyle />
          </Box>
        )}

        <Box
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: useActivityMatchedStyling ? 0 : "12px",
            flex: 1,
            overflowY: "auto",
            px: { xs: 1, sm: 1.5 },
            py: 1,
          }}
        >
          {loadingTimesheets ? (
            <Box
              sx={{
                minHeight: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Loading timesheet entries...
              </Typography>
            </Box>
          ) : groupedRows.length === 0 ? (
            <Box
              sx={{
                minHeight: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No timesheet entries found for the selected period.
              </Typography>
            </Box>
          ) : (
            <Profiler id="TimesheetsRows" onRender={onTimesheetsProfilerRender}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {!isMobile && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `${TIMESHEET_DAY_COLUMN_WIDTH} minmax(0, 1fr)`,
                    px: 1.5,
                    py: 1,
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.background.alt,
                    border: `1px solid ${theme.palette.primary.border2}`,
                    borderRadius: useActivityMatchedStyling ? "10px" : "12px",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>
                    Day
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: TIMESHEET_DESKTOP_COLUMNS,
                      gap: 1,
                      pl: 1.5,
                      borderLeft: `1px solid ${theme.palette.primary.border2}`,
                    }}
                  >
                    <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>Clock In</Typography>
                    <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>Clock Out</Typography>
                    <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>Duration</Typography>
                    <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>Status</Typography>
                    <Typography variant="caption" fontWeight={800} sx={{ letterSpacing: 0.6, textTransform: "uppercase" }}>Notes</Typography>
                  </Box>
                </Box>
              )}

              {groupedRows.map((group) => (
                <Box
                  key={group.dateLabel}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: `${TIMESHEET_DAY_COLUMN_WIDTH} minmax(0, 1fr)`,
                    },
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: useActivityMatchedStyling ? "12px" : "16px",
                    overflow: "hidden",
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: useActivityMatchedStyling ? "none" : theme.shadows[2],
                    transition: useActivityMatchedStyling
                      ? "background-color 0.2s ease"
                      : "box-shadow 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      boxShadow: useActivityMatchedStyling ? "none" : theme.shadows[4],
                      transform: useActivityMatchedStyling ? "none" : "translateY(-1px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: theme.palette.background.alt,
                      borderRight: { xs: "none", md: `1px solid ${theme.palette.divider}` },
                      borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: "none" },
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} color={theme.palette.primary.main}>
                      {group.dateLabel}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">
                      Total: {group.totalDurationLabel}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {group.entries.map((entry, entryIndex) => {
                      const isInProgress = entry.status === "In progress";

                      return (
                        <Box
                          key={entry.id}
                          role={allowEdit ? "button" : undefined}
                          tabIndex={allowEdit ? 0 : -1}
                          aria-label={`Edit timeslot ${formatTimeOnly(entry.clockInAt)} to ${formatTimeOnly(entry.clockOutAt)}`}
                          onClick={allowEdit ? () => handleEditTimeslot(entry.rawEntry) : undefined}
                          onKeyDown={(event) => {
                            if (!allowEdit) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleEditTimeslot(entry.rawEntry);
                            }
                          }}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              md: TIMESHEET_DESKTOP_COLUMNS,
                            },
                            gap: { xs: 1, md: 1 },
                            px: 1.5,
                            py: 1.25,
                            cursor: allowEdit ? "pointer" : "default",
                            backgroundColor: theme.palette.background.paper,
                            borderBottom:
                              entryIndex === group.entries.length - 1
                                ? "none"
                                : `1px solid ${theme.palette.primary.border2}`,
                            alignItems: "center",
                            transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                            "&:hover": {
                              backgroundColor: allowEdit
                                ? theme.palette.action.hover
                                : theme.palette.background.paper,
                            },
                            "&:focus-visible": {
                              outline: allowEdit
                                ? `2px solid ${theme.palette.primary.main}`
                                : "none",
                              outlineOffset: -2,
                            },
                          }}
                        >
                          <Box>
                            {isMobile && (
                              <Typography variant="caption" color="text.secondary">Clock In</Typography>
                            )}
                            <Typography variant="body2" fontWeight={600}>
                              {formatTimeOnly(entry.clockInAt)}
                            </Typography>
                          </Box>

                          <Box>
                            {isMobile && (
                              <Typography variant="caption" color="text.secondary">Clock Out</Typography>
                            )}
                            <Typography variant="body2" fontWeight={entry.clockOutAt ? 600 : 400}>
                              {entry.clockOutAt
                                ? formatTimeOnly(entry.clockOutAt)
                                : formatTimeOnly(entry.liveClockOutAt || now)}
                            </Typography>
                          </Box>

                          <Box>
                            {isMobile && (
                              <Typography variant="caption" color="text.secondary">Duration</Typography>
                            )}
                            <Typography
                              variant="body2"
                              color={entry.clockOutAt ? theme.palette.primary.main : theme.palette.info.main}
                              fontWeight={entry.clockOutAt ? 600 : 400}
                            >
                              {entry.duration}
                            </Typography>
                          </Box>

                          <Box>
                            {isMobile && (
                              <Typography variant="caption" color="text.secondary">Status</Typography>
                            )}
                            <Chip
                              label={entry.status}
                              size="small"
                              variant="filled"
                              sx={{
                                borderRadius: "8px",
                                fontWeight: 500,
                                backgroundColor: isInProgress
                                  ? theme.palette.info.main
                                  : theme.palette.primary.main,
                                color: isInProgress
                                  ? theme.palette.info.contrastText
                                  : theme.palette.common.white,
                                "& .MuiChip-label": {
                                  px: 1,
                                  color: isInProgress
                                    ? theme.palette.info.contrastText
                                    : theme.palette.common.white,
                                },
                              }}
                            />
                          </Box>

                          <Box sx={{ minWidth: 0 }}>
                            {isMobile && (
                              <Typography variant="caption" color="text.secondary">Notes</Typography>
                            )}
                            <Typography
                              variant="body2"
                              color={entry.notes && entry.notes !== "-" ? "text.primary" : "text.secondary"}
                              sx={{ wordBreak: "break-word" }}
                            >
                              {entry.notes}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
            </Profiler>
          )}
        </Box>

        {!shouldRenderTopStatsAndFilter && (
          <Box
            sx={{
              flexShrink: 0,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.alt,
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 1, sm: 1.25 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "stretch",
              gap: detailsPanelExpanded ? 1 : 0,
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1,
              }}
            >
              {!detailsPanelExpanded && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 2 },
                    minWidth: 0,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    Month total time: {monthlyStatsLabels.totalHours}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    Month total pay: {monthlyTotalLabel}
                  </Typography>
                </Box>
              )}

              <Box
                component="button"
                type="button"
                onClick={() => setDetailsPanelExpanded((prev) => !prev)}
                aria-label={detailsPanelExpanded ? "Collapse details" : "Expand details"}
                title={detailsPanelExpanded ? "Collapse details" : "Expand details"}
                sx={{
                  border: "none",
                  background: "none",
                  p: 0,
                  m: 0,
                  ml: "auto",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  lineHeight: 0,
                }}
              >
                {detailsPanelExpanded ? (
                  <ExpandMoreRoundedIcon fontSize="small" />
                ) : (
                  <ExpandLessRoundedIcon fontSize="small" />
                )}
              </Box>
            </Box>

            {detailsPanelExpanded ? (
              <>
                {numericHourlyRate <= 0 && (
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={theme.palette.warning.main}
                    sx={{ textAlign: "center" }}
                  >
                    {hourlyRateSubject} hourly rate is currently $0.00. Please update the rate in Settings to ensure totals are calculated correctly.
                  </Typography>
                )}
                <Grid container spacing={2} sx={{ width: "100%" }}>
                  {timesheetStatsCards.map((statCard) => {
                    const StatIcon = statCard.icon;
                    return (
                      <Grid key={statCard.key} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Card
                          elevation={2}
                          sx={{
                            borderRadius: "12px",
                            height: "100%",
                          }}
                        >
                          <CardContent
                            sx={{
                              textAlign: "center",
                              minHeight: 156,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <StatIcon
                              sx={{
                                fontSize: 40,
                                color: statCard.color,
                                mb: 1,
                              }}
                            />
                            <Typography variant={statCard.valueVariant} fontWeight="bold">
                              {statCard.value}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {statCard.label}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  );
});

TimesheetsTab.displayName = "TimesheetsTab";

export default TimesheetsTab;
